
import { computed, defineComponent, onMounted, onUnmounted, ref } from 'vue'
import { ColorPicker } from 'vue-color-kit'
import BaseModal from '@/components/Modal/BaseModal.vue'
import SettingsGroup from '@/components/Modal/SettingsModal/SettingsGroup.vue'
import Setting from '@/components/Modal/SettingsModal/Setting.vue'
import Number from '@/components/Common/Forms/Number.vue'
import TGSelect from '@/components/Common/Forms/TGSelect.vue'
import { Orientation } from '@/domain/models/TrinketSettings'
import TgToggle from '@/components/Common/Forms/TgToggle.vue'
import { useStore } from '@/store'
import TgButton from '@/components/Common/Forms/TgButton.vue'
import { loadFile } from '@/domain/Utils'

export default defineComponent({
    name: 'SettingsModal',
    components: {
        TgButton,
        TgToggle,
        TGSelect,
        Number,
        Setting,
        SettingsGroup,
        BaseModal,
        ColorPicker
    },
    setup () {
        const store = useStore()
        const showModal = computed(() => store.state.settingsModal.show)
        const gridMeasure = computed({
            get () {
                return store.state.configuration.grid.width
            },
            set (value: number) {
                store.dispatch('updateGridConfiguration', { width: value, height: value })
            }
        })
        const gridOrientationOptions = [{
            description: 'Vertical',
            value: Orientation.PORTRAIT
        },
        {
            description: 'Horizontal',
            value: Orientation.LANDSCAPE
        }]
        const gridOrientation = computed({
            get () {
                return store.state.configuration.grid.orientation
            },
            set (value: Orientation) {
                store.dispatch('updateGridConfiguration', { orientation: value })
            }
        })
        const showGridHelper = computed({
            get () {
                return store.state.configuration.grid.showGridHelper
            },
            set (value: boolean) {
                store.dispatch('updateGridConfiguration', { showGridHelper: value })
            }
        })
        const showGridHelperColorPicker = ref(false)
        const helperColorPicker = computed({
            get () {
                return store.state.configuration.grid.gridHelperColor
            },
            set (value: string) {
                store.dispatch('updateGridConfiguration', { gridHelperColor: value })
            }
        })
        const showGridAxis = computed({
            get () {
                return store.state.configuration.grid.showGridAxis
            },
            set (value: boolean) {
                store.dispatch('updateGridConfiguration', { showGridAxis: value })
            }
        })
        const showGridAxisColorPicker = ref(false)
        const axisColorPicker = computed({
            get () {
                return store.state.configuration.grid.gridAxisColor
            },
            set (value: string) {
                store.dispatch('updateGridConfiguration', { gridAxisColor: value })
            }
        })
        const autoSaveActive = computed({
            get () {
                return store.state.configuration.saving.autoSave.active
            },
            set (value: boolean) {
                store.dispatch('updateAutoSaveConfiguration', { active: value })
            }
        })
        const autoSaveTimeInterval = computed({
            get () {
                return store.state.configuration.saving.autoSave.timeInterval / 60000
            },
            set (value: number) {
                store.dispatch('updateAutoSaveConfiguration', { timeInterval: value * 60000 })
            }
        })
        const saveOnExit = computed({
            get () {
                return store.state.configuration.saving.saveOnExit
            },
            set (value) {
                store.dispatch('updateSaveOnExitConfiguration', value)
            }
        })
        const continueLine = computed({
            get () {
                return store.state.configuration.line.continueLine
            },
            set (value) {
                store.dispatch('updateContinueLineConfiguration', value)
            }
        })

        const clickOutside = (e: Event) => {
            const el = e.target as HTMLElement

            if (!el.classList.contains('color-picker-box') && !el.closest('.color-picker-box') && !el.classList.contains('color-picker') && !el.closest('.color-picker')) {
                showGridHelperColorPicker.value = false
            }
        }

        const closeModal = () => {
            store.dispatch('closeSettings')
        }

        const savePalette = () => {
            store.dispatch('exportPalette')
        }

        const loadPalette = async (e: Event) => {
            const files = (e.target as HTMLInputElement).files

            const res = await loadFile(files)
            store.dispatch('loadPalette', (res.target as FileReader).result)
        }

        onMounted(() => {
            document.addEventListener('click', clickOutside)
        })

        onUnmounted(() => {
            document.removeEventListener('click', clickOutside)
        })

        return {
            closeModal,
            savePalette,
            loadPalette,
            gridMeasure,
            gridOrientation,
            gridOrientationOptions,
            showGridHelper,
            showGridAxis,
            showGridAxisColorPicker,
            axisColorPicker,
            showGridHelperColorPicker,
            helperColorPicker,
            autoSaveActive,
            autoSaveTimeInterval,
            saveOnExit,
            continueLine,
            showModal
        }
    }
})
